import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { PARAMETER } from '../../../../../constants/parameter-name-for-update';
import InputValueProps from '../../../../../prop-types/input-value-props';
import ShippingServiceSelector from '../../../../../../../../components/shipping-service-selector';
import GroupedCarrierPackageDropdown from '../../../../../../../../components/grouped-carrier-package-dropdown';
import useServiceCollectionGroup from '../../../../../../../../components/shipping-service-selector/group-hooks/use-service-collection-group';
import { useRateShopper } from '../../../../../../rate-shopper/hooks/use-rate-shopper';
import useSelectedItemName from '../../../../../../../../components/shipping-service-selector/use-selected-item-name';
import { ShippingStrategyDefaultTagWithName } from '../../../../../../../../components/shipping-strategy-default-tag/shipping-strategy-default-tag-with-name';
import style from './style.module.less';
import messages from './messages';

const SetServiceCollection = ({ inputValueProps, onChange, className }) => {
  const { parameter } = inputValueProps;

  const valueParts = useMemo(() => parameter?.toString().split(',') || [], [parameter]);
  const selectedItem = useMemo(
    () => ({
      serviceCollectionGuid: valueParts[0],
    }),
    [valueParts],
  );
  const { requirePackage } = useRateShopper();
  const [packageId, setPackageId] = useState(!requirePackage ? valueParts[1] : '');
  const name = useSelectedItemName(selectedItem);
  const serviceCollectionGroup = useServiceCollectionGroup(selectedItem);
  const onValueChange = useCallback(
    (newServiceCollection) => {
      // When Service collection has requirePackage, that means that user is not able to change it so we don't need to send that information
      const newServiceCollectionParameter = !requirePackage
        ? `${newServiceCollection.serviceCollectionGuid},${packageId}`
        : newServiceCollection.serviceCollectionGuid;
      onChange({
        [PARAMETER]: newServiceCollectionParameter,
      });
    },
    [onChange, packageId, requirePackage],
  );
  const serviceGroups = useMemo(() => [serviceCollectionGroup], [serviceCollectionGroup]);
  const { items } = serviceGroups[0];
  const selectedShippingStrategy = items.find((item) => item.serviceCollectionGuid === selectedItem.serviceCollectionGuid);
  const { isTemplate } = selectedShippingStrategy || {};
  const selectedItemName = !isTemplate ? name : <ShippingStrategyDefaultTagWithName name={name} />;
  const onSetPackageId = useCallback(
    (packId) => {
      const isCustomPackage = packId?.indexOf('-') >= 0;
      const parsedPackageId = isCustomPackage ? packId.substring(0, packId.indexOf('-')) : packId;
      setPackageId(parsedPackageId);
      onChange({
        [PARAMETER]: `${selectedItem.serviceCollectionGuid},${parsedPackageId}`,
      });
    },
    [onChange, selectedItem],
  );

  return (
    <div className={style['multi-control-row']}>
      <div>
        <FormattedMessage {...messages.serviceCollection} />
      </div>
      <ShippingServiceSelector
        dropdownMenuShouldHaveWidthOfButton
        dropdownTogglerClassName={className}
        onSelected={onValueChange}
        selectedItemName={selectedItemName}
        serviceGroups={serviceGroups}
      />
      {/* When Service collection has requirePackage, that means that user is not able to change it so selector must be hide */}
      {!requirePackage && (
        <div className={classnames(style.stacked, style['multi-control-row'])}>
          <div>
            <FormattedMessage {...messages.packageLabel} />
          </div>
          <GroupedCarrierPackageDropdown
            dropdownTogglerClassName={className}
            onChange={onSetPackageId}
            selectedPackageId={packageId}
            showGenericPackages={!!selectedItem.serviceCollectionGuid}
          />
        </div>
      )}
    </div>
  );
};

SetServiceCollection.propTypes = {
  inputValueProps: InputValueProps.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SetServiceCollection;
